/**
 * Reports browser-side crashes to the server.
 *
 * Vue runs component-event listeners through callWithErrorHandling, which
 * swallows whatever they throw — it reaches the console and nowhere else. That
 * is how a TypeError that broke every file attach in the reply box stayed
 * invisible for a full day: no alert for the agent, no request, no server log.
 * Posting crashes here gives the server something to alert on.
 */

const ENDPOINT = '/api/v1/frontend_error';

// A broken render can throw on every frame, so cap what one page load may send.
const MAX_REPORTS_PER_PAGE_LOAD = 10;

const reportedFingerprints = new Set();
let reportCount = 0;

const truncate = (value, length) => String(value ?? '').slice(0, length);

const send = payload => {
  if (reportCount >= MAX_REPORTS_PER_PAGE_LOAD) return;

  // Recording the fingerprint before sending also bounds re-entrancy: an error
  // raised by the reporter itself is either a repeat (deduped here) or one of
  // at most MAX_REPORTS_PER_PAGE_LOAD.
  const fingerprint = `${payload.message}|${payload.source}`;
  if (reportedFingerprints.has(fingerprint)) return;

  reportedFingerprints.add(fingerprint);
  reportCount += 1;

  // window.axios carries the auth headers and is set up in the entrypoint.
  window.axios?.post(ENDPOINT, payload).catch(() => {});
};

export const reportFrontendError = (error, { kind, source = '' } = {}) =>
  send({
    kind,
    source: truncate(source, 200),
    url: truncate(window.location.pathname, 200),
    message: truncate(error?.message ?? error, 300),
  });

/**
 * Chains onto whatever error handler is already installed (Sentry's, when a DSN
 * is configured) rather than replacing it.
 * @param {Object} app - the Vue application instance
 */
export const registerFrontendErrorReporter = app => {
  const existingHandler = app.config.errorHandler;

  app.config.errorHandler = (error, instance, info) => {
    reportFrontendError(error, { kind: 'vue', source: info });

    if (existingHandler) {
      existingHandler(error, instance, info);
    } else {
      // Setting a handler suppresses Vue's own logging; keep the console noisy.
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  window.addEventListener('error', event => {
    reportFrontendError(event.error ?? event.message, {
      kind: 'window',
      source: `${event.filename}:${event.lineno}`,
    });
  });

  window.addEventListener('unhandledrejection', event => {
    reportFrontendError(event.reason, { kind: 'promise' });
  });
};
